/**
 * LandingThemeContext - Context for managing landing page visual themes
 *
 * Provides three theme variants:
 * - elegant: Subtle, professional animations
 * - cyber: Tech/gaming aesthetic with neon effects
 * - balanced: Best of both worlds
 */

"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export type LandingTheme = "elegant" | "cyber" | "balanced";

interface LandingThemeContextValue {
  theme: LandingTheme;
  setTheme: (theme: LandingTheme) => void;
}

const LandingThemeContext = createContext<LandingThemeContextValue | null>(
  null
);

const STORAGE_KEY = "landing-theme";

interface LandingThemeProviderProps {
  children: ReactNode;
  defaultTheme?: LandingTheme;
}

export function LandingThemeProvider({
  children,
  defaultTheme = "balanced",
}: LandingThemeProviderProps) {
  const [theme, setThemeState] = useState<LandingTheme>(defaultTheme);
  const [mounted, setMounted] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as LandingTheme | null;
    if (stored && ["elegant", "cyber", "balanced"].includes(stored)) {
      setThemeState(stored);
    }
    setMounted(true);
  }, []);

  // Persist theme to localStorage
  const setTheme = (newTheme: LandingTheme) => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  };

  // Update CSS custom property for theme-aware styles
  useEffect(() => {
    if (mounted) {
      document.documentElement.setAttribute("data-landing-theme", theme);
    }
  }, [theme, mounted]);

  return (
    <LandingThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </LandingThemeContext.Provider>
  );
}

export function useLandingTheme() {
  const context = useContext(LandingThemeContext);
  if (!context) {
    throw new Error(
      "useLandingTheme must be used within a LandingThemeProvider"
    );
  }
  return context;
}
